import type React from "react";
import { forwardRef } from "react";
import { Focusable } from "react-aria-components";

import { Avatar, getAvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { getRandomColor } from "@/components/ui/utils";

type UserAvatarProps = {
  user?: {
    id: number;
    username?: string | null;
    email?: string | null;
  } | null;
  additionalTooltipText?: string;
  tooltip?: boolean;
  fallback?: React.ReactNode;
  className?: string;
} & Pick<React.ComponentProps<typeof Avatar>, "size">;

export const UserAvatar = forwardRef<
  React.ElementRef<typeof Avatar>,
  UserAvatarProps
>(
  (
    {
      user,
      additionalTooltipText,
      size = "medium",
      tooltip = false,
      className,
      fallback,
    },
    ref,
  ) => {
    const name = user?.username || user?.email;
    const additionalText = additionalTooltipText
      ? ` ${additionalTooltipText}`
      : "";
    const avatar = (
      <Avatar
        color={getRandomColor(user?.id)}
        size={size}
        initials={getAvatarFallback(name)}
        ref={ref}
        className={className}
        fallback={fallback}
        role={tooltip ? "button" : undefined}
      />
    );
    return tooltip ? (
      <TooltipTrigger>
        <Focusable>{avatar}</Focusable>
        <Tooltip>
          {name}
          {additionalText}
        </Tooltip>
      </TooltipTrigger>
    ) : (
      avatar
    );
  },
);
